import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Alert } from 'react-native';
import { Text, IconButton, Portal, Dialog, RadioButton, Chip } from 'react-native-paper';
import { useLocalSearchParams, useRouter } from 'expo-router';
import BaseContainer from '@/components/BaseContainer';
import Button from '@/components/Button';
import ScreenMargin from '@/components/ScreenMargin';
import { useToast } from 'react-native-toast-notifications';
import { useTranslation } from 'react-i18next';
import { Color, Color as ThemeColors } from '@/GlobalStyles';
import { useForm } from 'react-hook-form';
import Form from '@/components/Form/Form';
import TextInput from '@/components/Form/TextInput';
import { AvailableValidationRules } from '@/components/Form/validation';
import useFormStyle from '@/components/Styles/useFormStyle';
import { useUserContext } from '@/components/Auth/UserContext';
import { useAuthContext } from '@/components/Auth/AuthContext';
import {
  useGetHelpTextsQuery,
  useUpsertHelpTextMutation,
  useUpdateHelpTextMutation,
  useDeleteHelpTextMutation,
  HelpTextsInsertInput,
  useGetHelpTextByFieldQuery,
} from '@/data/data';
import { getFieldLabel } from '@/utils/helpTextsUtils';
import { useApolloClient } from '@apollo/client';
import { getAdminLink, getNormalLink } from '@/components/Apollo/client';
import FormSectionFooter from '@/components/Form/FormSectionFooter';

interface FormData {
  field_name: string;
  text_fr: string;
  text_en?: string;
}

export default function AideContextuelleEditScreen() {
  const { fieldName } = useLocalSearchParams<{ fieldName: string }>();
  const router = useRouter();
  const { t } = useTranslation();
  const toast = useToast();
  const styles = useFormStyle();
  const { user } = useUserContext();
  const authUser = useAuthContext();
  const client = useApolloClient();

  const [existingPersonalization, setExistingPersonalization] = useState<any>(null);
  const [existingDefault, setExistingDefault] = useState<any>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editMode, setEditMode] = useState<'personal' | 'default'>('personal');
  // Check admin status from cognito groups like in implants page
  const isAdmin =
    authUser.payload &&
    authUser.payload['cognito:groups'] &&
    authUser.payload['cognito:groups'].indexOf('admin') > -1;

  const form = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      field_name: fieldName || '',
      text_fr: '',
      text_en: '',
    },
  });

  // Récupérer les données existantes
  const { data } = useGetHelpTextsQuery({
    variables: {
      fieldName: fieldName,
    },
    skip: !fieldName,
  });

  // Pré-remplir le formulaire avec les données existantes
  useEffect(() => {
    if (data?.helpTexts && fieldName) {
      const texts = data.helpTexts.filter((text: any) => text.fieldName === fieldName);
      const customText = texts.find((t: any) => t.chirurgienId === user?.id);
      const defaultText = texts.find((t: any) => t.isDefault);

      setExistingPersonalization(customText);
      setExistingDefault(defaultText);

      console.log('isAdmin:', isAdmin);
      console.log('defaultText:', defaultText);
      console.log('authUser.payload:', authUser.payload);

      // Si admin et qu'il y a un texte par défaut, proposer le choix
      if (isAdmin && defaultText) {
        // Si l'admin a une personnalisation, on lui propose de choisir
        // Sinon on lui propose quand même de modifier le texte par défaut
        setDialogVisible(true);

        // Charger temporairement le texte existant
        const textToLoad = customText || defaultText;
        if (textToLoad) {
          form.setValue('field_name', fieldName);
          form.setValue('text_fr', textToLoad.textFr || '');
          form.setValue('text_en', textToLoad.textEn || '');
        }
      } else {
        // Utilisateur normal ou pas de texte par défaut
        const textToLoad = customText || defaultText;
        if (textToLoad) {
          form.setValue('field_name', fieldName);
          form.setValue('text_fr', textToLoad.textFr || '');
          form.setValue('text_en', textToLoad.textEn || '');
        }
      }
    }
  }, [data, fieldName, user?.id, isAdmin]);

  const [upsertHelpText, { loading }] = useUpsertHelpTextMutation({
    fetchPolicy: 'network-only',
  });
  const [updateHelpText, { loading: updateLoading }] = useUpdateHelpTextMutation({
    fetchPolicy: 'network-only',
  });
  const [deleteHelpText, { loading: deleteLoading }] = useDeleteHelpTextMutation({
    fetchPolicy: 'network-only',
  });
  const handleSave = form.handleSubmit(async (data) => {
    if (!fieldName || !user?.id) {
      toast.show(t('Erreur: données manquantes'), { type: 'danger' });
      return;
    }

    try {
      // Si on modifie le texte par défaut en mode admin
      if (isAdmin && editMode === 'default' && existingDefault) {
        // Utiliser le client admin
        client.setLink(getAdminLink());

        // Utiliser la mutation update pour modifier le texte par défaut existant
        await updateHelpText({
          variables: {
            id: existingDefault.id,
            textFr: data.text_fr || '',
            textEn: data.text_en || '',
          },
        });

        // Restaurer le client normal
        client.setLink(getNormalLink());
      } else {
        // Création ou modification d'une personnalisation
        const object: HelpTextsInsertInput = {
          fieldName: fieldName,
          textFr: data.text_fr || '',
          textEn: data.text_en || '',
          chirurgienId: user.id, // Lié à l'utilisateur pour les personnalisations
        };

        // Si on a déjà une personnalisation, utiliser update au lieu d'upsert
        if (existingPersonalization) {
          await updateHelpText({
            variables: {
              id: existingPersonalization.id,
              textFr: data.text_fr || '',
              textEn: data.text_en || '',
            },
          });
        } else {
          // Créer une nouvelle personnalisation
          await upsertHelpText({
            variables: { object },
          });
        }
      }

      toast.show(t("Texte d'aide enregistré"), { type: 'success' });
      router.back();
    } catch (error) {
      console.error('Save error:', error);
      toast.show(t("Erreur lors de l'enregistrement"), { type: 'danger' });
      // S'assurer de restaurer le client normal même en cas d'erreur
      if (isAdmin && editMode === 'default') {
        client.setLink(getNormalLink());
      }
    }
  });

  const handleDelete = () => {
    if (!existingPersonalization?.id) return;

    Alert.alert(
      t('Confirmation'),
      t('Voulez-vous vraiment supprimer votre personnalisation et revenir au texte par défaut ?'),
      [
        { text: t('Annuler'), style: 'cancel' },
        {
          text: t('Supprimer'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteHelpText({
                variables: { id: existingPersonalization.id },
              });
              toast.show(t('Personnalisation supprimée'), { type: 'success' });
              router.back();
            } catch (error) {
              console.error('Delete error:', error);
              toast.show(t('Erreur lors de la suppression'), { type: 'danger' });
            }
          },
        },
      ]
    );
  };

  return (
    <ScreenMargin>
      <BaseContainer withScrollView={'keyboardAware'}>
        <Text style={styles?.headerForm}>
          {editMode === 'default'
            ? t('Modifier le texte par défaut')
            : existingPersonalization
              ? t("Modifier le texte d'aide")
              : t("Personnaliser le texte d'aide")}
        </Text>
        <Form {...form}>
          <View style={styles.bodyForm}>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, color: ThemeColors.MainGris }}>
                  {getFieldLabel(fieldName || '', t)}
                </Text>
                {editMode === 'default' && isAdmin && (
                  <Chip
                    icon="shield-account"
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: 8,
                      backgroundColor: '#FFA50020',
                    }}
                  >
                    {t('Mode administrateur')}
                  </Chip>
                )}
              </View>
              {existingPersonalization && editMode === 'personal' && (
                <IconButton
                  icon="delete"
                  size={24}
                  iconColor={ThemeColors.error}
                  onPress={handleDelete}
                />
              )}
            </View>

            <View style={{ marginBottom: 16 }}>
              <Text variant="labelLarge" style={{ marginBottom: 8 }}>
                {t('Nom du champ')}
              </Text>
              <View
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: '#e0e0e0',
                }}
              >
                <Text>{fieldName}</Text>
              </View>
            </View>

            <TextInput
              name="text_fr"
              label={t('Texte en français')}
              multiline
              numberOfLines={6}
              validators={[AvailableValidationRules.required]}
              withMargin={true}
              autoFocus={false}
            />

            <TextInput
              name="text_en"
              label={t('Texte en anglais (optionnel)')}
              multiline
              numberOfLines={6}
              withMargin={true}
              autoFocus={false}
            />
            <View style={styles?.footerForm}>
              <FormSectionFooter
                firstPrevLabel={t('Annuler')}
                firstPrevOnPress={() => router.back()}
                saveLabel={t('Enregistrer')}
                saveLoading={loading}
                saveDisabled={loading || updateLoading}
                onPress={handleSave}
              />
            </View>
          </View>
        </Form>

        <Portal>
          <Dialog
            visible={dialogVisible}
            onDismiss={() => setDialogVisible(false)}
            style={{
              backgroundColor: 'white',
              borderRadius: 16,
              margin: 20,
            }}
          >
            <Dialog.Title style={{ fontSize: 20, fontWeight: '600', color: ThemeColors.MainBleu }}>
              {t('Choisir le texte à modifier')}
            </Dialog.Title>
            <Dialog.Content style={{ paddingHorizontal: 24 }}>
              <Text style={{ marginBottom: 16 }}>
                {t(
                  "En tant qu'administrateur, vous pouvez modifier le texte par défaut pour tous les utilisateurs ou créer votre propre personnalisation."
                )}
              </Text>
              <RadioButton.Group
                onValueChange={(value) => setEditMode(value as 'personal' | 'default')}
                value={editMode}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
                  <RadioButton.Android
                    uncheckedColor={Color.colorGray_100}
                    background={Color.BlancCasse}
                    value="personal"
                  />
                  <Text>
                    {existingPersonalization
                      ? t('Modifier ma personnalisation')
                      : t('Créer ma personnalisation')}
                  </Text>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <RadioButton.Android
                    uncheckedColor={Color.colorGray_100}
                    background={Color.BlancCasse}
                    value="default"
                  />
                  <Text>{t('Modifier le texte par défaut (tous les utilisateurs)')}</Text>
                </View>
              </RadioButton.Group>
            </Dialog.Content>
            <Dialog.Actions style={{ paddingHorizontal: 16, paddingBottom: 16 }}>
              <Button
                mode="text"
                onPress={() => router.back()}
                style={{ marginRight: 8 }}
                contentStyle={{ paddingHorizontal: 10 }}
                textColor={ThemeColors.MainGris}
              >
                {t('Annuler')}
              </Button>
              <Button
                mode="contained"
                onPress={() => {
                  setDialogVisible(false);
                  // Charger le texte approprié
                  const textToLoad =
                    editMode === 'default' ? existingDefault : existingPersonalization;
                  if (textToLoad) {
                    form.setValue('text_fr', textToLoad.textFr || '');
                    form.setValue('text_en', textToLoad.textEn || '');
                  }
                }}
                contentStyle={{ paddingHorizontal: 10 }}
              >
                {t('Continuer')}
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
      </BaseContainer>
    </ScreenMargin>
  );
}
